/**
 * Platform Assistant Chatbot Page
 * 
 * Two-panel layout with session list sidebar and conversation view.
 * Route: /assistant
 */
import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertCircle, ChevronDown, Eye, Sparkles } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useSessionTokens } from '@/api/orchestrator'
import { SessionTokenUsage } from '@/components/chatbot/SessionTokenUsage'
import { PageHeader } from '@/components/common/PageHeader'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Button } from '@/components/ui/button'
import { ChatbotSessionList } from '@/components/chatbot/ChatbotSessionList'
import { ChatbotConversation } from '@/components/chatbot/ChatbotConversation'
import { ContextItemsSection } from '@/components/chatbot/ContextItemsSection'
import { AddContextItemDialog } from '@/components/chatbot/AddContextItemDialog'
import {
  listChatbotSessions,
  createChatbotSession,
  deleteChatbotSession,
  getChatbotHistory,
  getChatbotSession,
  addContextItem,
  removeContextItem,
  updateSessionProject,
} from '@/api/chatbot'
import type { ChatbotSession, ChatbotMessage, SessionContextItem } from '@/types/chatbot'

/**
 * Token usage display for a chatbot session
 */
function TokenUsageDisplay({ sessionId }: { sessionId: string }) {
  const { data: tokenUsage, isLoading, error } = useSessionTokens(sessionId, {
    refetchInterval: 30000, // Refresh every 30 seconds during conversation
  })

  return (
    <SessionTokenUsage
      tokenUsage={tokenUsage}
      isLoading={isLoading}
      error={error?.message}
      variant="compact"
      className="min-w-[180px]"
    />
  )
}

export function ChatbotPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedSessionId = searchParams.get('session')

  // Wait for user provisioning to complete before making API calls
  // This prevents "Permission denied" errors for first-time visitors
  const { data: currentUser, isLoading: isUserLoading } = useCurrentUser()

  // State
  const [sessions, setSessions] = useState<ChatbotSession[]>([])
  const [currentSession, setCurrentSession] = useState<ChatbotSession | null>(null)
  const [currentMessages, setCurrentMessages] = useState<ChatbotMessage[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(true)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [isAddContextDialogOpen, setIsAddContextDialogOpen] = useState(false)
  const [isContextOpen, setIsContextOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Auto-expand context when items are added, collapse when empty
  useEffect(() => {
    const hasItems = (currentSession?.contextItems?.length ?? 0) > 0
    setIsContextOpen(hasItems)
  }, [currentSession?.contextItems?.length])

  // Load sessions once user is ready (provisioned)
  useEffect(() => {
    if (currentUser) {
      loadSessions()
    }
  }, [currentUser])

  // Load messages and session details when selected session changes
  useEffect(() => {
    if (selectedSessionId && currentUser) {
      loadMessages(selectedSessionId)
      loadSessionDetails(selectedSessionId)
    } else if (!selectedSessionId) {
      setCurrentMessages([])
      setCurrentSession(null)
    }
  }, [selectedSessionId, currentUser])

  const loadSessions = async () => {
    setIsLoadingSessions(true)
    setError(null)
    try {
      const response = await listChatbotSessions()
      setSessions(response.sessions)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions')
    } finally {
      setIsLoadingSessions(false)
    }
  }

  const loadSessionDetails = async (sessionId: string) => {
    try {
      const session = await getChatbotSession(sessionId)
      setCurrentSession(session)
    } catch (err) {
      console.warn('Failed to load session details:', err)
      setCurrentSession(null)
    }
  }

  const loadMessages = async (sessionId: string) => {
    setIsLoadingMessages(true)
    try {
      const response = await getChatbotHistory(sessionId)
      setCurrentMessages(response.messages)
    } catch (err) {
      toast.error('Failed to load conversation history')
      setCurrentMessages([])
    } finally {
      setIsLoadingMessages(false)
    }
  }

  const handleAddContextItem = useCallback(async (type: string, value: string) => {
    if (!selectedSessionId) return
    
    try {
      const response = await addContextItem(selectedSessionId, { type, value })
      // Update current session with new context item
      setCurrentSession((prev) => prev ? {
        ...prev,
        contextItems: [...(prev.contextItems || []), response.contextItem],
      } : null)
      toast.success('Context item added')
    } catch (err) {
      toast.error('Failed to add context item')
      throw err
    }
  }, [selectedSessionId])

  const handleRemoveContextItem = useCallback(async (item: SessionContextItem) => {
    if (!selectedSessionId) return
    
    try {
      await removeContextItem(selectedSessionId, { type: item.type, value: item.value })
      // Update current session without the removed item
      setCurrentSession((prev) => prev ? {
        ...prev,
        contextItems: (prev.contextItems || []).filter(
          (i) => !(i.type === item.type && i.value === item.value)
        ),
      } : null)
      toast.success('Context item removed')
    } catch (err) {
      toast.error('Failed to remove context item')
      throw err
    }
  }, [selectedSessionId])

  const handleSelectSession = useCallback((sessionId: string) => {
    setSearchParams({ session: sessionId })
  }, [setSearchParams])

  const handleNewSession = useCallback(async () => {
    try {
      const response = await createChatbotSession()
      // Add new session to list
      setSessions((prev) => [{
        sessionId: response.sessionId,
        title: response.title,
        status: 'active',
        createdAt: response.createdAt,
        updatedAt: response.createdAt,
        messageCount: 0,
      }, ...prev])
      // Select the new session
      setSearchParams({ session: response.sessionId })
      toast.success('New conversation created')
    } catch (err) {
      toast.error('Failed to create new conversation')
    }
  }, [setSearchParams])

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    try {
      await deleteChatbotSession(sessionId)
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId))
      // If we deleted the selected session, clear the selection
      if (sessionId === selectedSessionId) {
        setSearchParams({})
        setCurrentMessages([])
      }
      toast.success('Conversation deleted')
    } catch (err) {
      toast.error('Failed to delete conversation')
    }
  }, [selectedSessionId, setSearchParams])

  const handleProjectChange = useCallback(async (sessionId: string, projectId: string | null) => {
    try {
      const result = await updateSessionProject(sessionId, projectId)
      // Update session in local state
      setSessions((prev) =>
        prev.map((session) =>
          session.sessionId === sessionId
            ? {
                ...session,
                projectId: result.projectId ?? undefined,
                projectName: result.projectName ?? undefined,
              }
            : session
        )
      )
      // Also update current session if it's the one being modified
      if (currentSession?.sessionId === sessionId) {
        setCurrentSession((prev) =>
          prev
            ? {
                ...prev,
                projectId: result.projectId ?? undefined,
                projectName: result.projectName ?? undefined,
              }
            : null
        )
      }
      toast.success(projectId ? 'Project linked' : 'Project unlinked')
    } catch (err) {
      toast.error('Failed to update project')
    }
  }, [currentSession])

  const handleMessagesUpdate = useCallback((messages: ChatbotMessage[]) => {
    setCurrentMessages(messages)
    // Update session in the list (last message, message count, timestamp)
    if (selectedSessionId && messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      setSessions((prev) =>
        prev.map((session) =>
          session.sessionId === selectedSessionId
            ? {
                ...session,
                lastMessage: lastMessage.content.slice(0, 100),
                messageCount: messages.length,
                updatedAt: new Date().toISOString(),
                // Update title from first user message if not set
                title: session.title ?? (
                  messages.find((m) => m.role === 'user')?.content.slice(0, 50) || 'New Conversation'
                ),
              }
            : session
        )
      )
    }
  }, [selectedSessionId])

  const handleConversationError = useCallback((err: Error) => {
    toast.error(err.message || 'An error occurred')
  }, [])

  // Error state
  if (error) {
    return (
      <PageShell>
        <PageHeader title="Ignix67 Assistant" />
        <Alert variant="destructive" className="max-w-md mx-auto mt-8">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </PageShell>
    )
  }

  return (
    <PageShell className="!p-0 !overflow-hidden">
      {/* Two-panel layout */}
      <div className="flex h-full">
        {/* Left panel — Session list */}
        <aside className="w-[280px] min-w-[280px] max-w-[280px] flex-shrink-0 border-r overflow-hidden isolate">
          <ChatbotSessionList
            sessions={sessions}
            selectedSessionId={selectedSessionId ?? undefined}
            isLoading={isUserLoading || isLoadingSessions}
            onSelectSession={handleSelectSession}
            onNewSession={handleNewSession}
            onDeleteSession={handleDeleteSession}
            onProjectChange={handleProjectChange}
          />
        </aside>

        {/* Right panel — Conversation */}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden isolate">
          {/* Header */}
          <div className="border-b px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h1 className="text-base font-semibold leading-tight">Ignix67 Assistant</h1>
                  <p className="text-xs text-muted-foreground">
                    ADO work items · Code search · Project management
                  </p>
                </div>
              </div>
              {selectedSessionId && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/sessions/${selectedSessionId}/context`}>
                      <Eye className="mr-1.5 h-3.5 w-3.5" />
                      Context
                    </Link>
                  </Button>
                  <TokenUsageDisplay sessionId={selectedSessionId} />
                </div>
              )}
            </div>
          </div>

          {/* Conversation area */}
          <div className="flex-1 min-h-0">
            {selectedSessionId ? (
              isLoadingMessages ? (
                <div className="p-6 space-y-4">
                  <Skeleton className="h-12 w-3/4" />
                  <Skeleton className="h-8 w-1/2 ml-auto" />
                  <Skeleton className="h-16 w-2/3" />
                </div>
              ) : (
                <ChatbotConversation
                  sessionId={selectedSessionId}
                  messages={currentMessages}
                  onMessagesUpdate={handleMessagesUpdate}
                  onError={handleConversationError}
                />
              )
            ) : (
              // No session selected - show welcome screen
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <div className="rounded-2xl bg-primary/5 border border-primary/10 p-6 mb-6">
                  <Sparkles className="h-12 w-12 text-primary/60" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Welcome to Ignix67 Assistant</h2>
                <p className="text-muted-foreground max-w-md mb-8 leading-relaxed">
                  Your AI-powered assistant for the Ignix67 platform. Ask about Azure DevOps
                  work items, search code, manage projects, and create automated coding tasks.
                </p>
                <div className="grid gap-2 text-sm text-muted-foreground mb-8">
                  <p className="rounded-lg bg-muted/50 px-4 py-2">"What work items are assigned to me?"</p>
                  <p className="rounded-lg bg-muted/50 px-4 py-2">"Search for authentication code in the backend repo"</p>
                  <p className="rounded-lg bg-muted/50 px-4 py-2">"List my projects"</p>
                  <p className="rounded-lg bg-muted/50 px-4 py-2">"Create a task to implement the login feature"</p>
                </div>
                <button
                  onClick={handleNewSession}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors shadow-sm"
                >
                  Start a New Conversation
                </button>
              </div>
            )}
          </div>

          {/* Context panel - collapsible */}
          {selectedSessionId && currentSession && (
            <Collapsible open={isContextOpen} onOpenChange={setIsContextOpen}>
              <div className="border-t bg-muted/30">
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full flex items-center justify-between px-4 py-2 h-auto"
                  >
                    <span className="text-sm font-medium">
                      Context {(currentSession.contextItems?.length ?? 0) > 0 && (
                        <span className="text-muted-foreground ml-1">
                          ({currentSession.contextItems?.length})
                        </span>
                      )}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isContextOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-4 pb-3">
                    <ContextItemsSection
                      items={currentSession.contextItems || []}
                      onAdd={() => setIsAddContextDialogOpen(true)}
                      onRemove={handleRemoveContextItem}
                      showAddButton
                      title=""
                      compact
                    />
                  </div>
                </CollapsibleContent>
              </div>
              <AddContextItemDialog
                open={isAddContextDialogOpen}
                onOpenChange={setIsAddContextDialogOpen}
                onAdd={async (type, value) => {
                  await handleAddContextItem(type, value)
                  setIsAddContextDialogOpen(false)
                }}
              />
            </Collapsible>
          )}
        </main>
      </div>
    </PageShell>
  )
}
